import { useState } from 'react';
import {
  CREDENTIAL_REQUIREMENTS,
  isMissingCredentialError,
  type MissingCredentialError,
} from './credentialRequirements';

export interface CredentialPromptState {
  isOpen: boolean;
  integration: string;
  integrationLabel: string;
  fields: { key: string; label: string; type: 'text' | 'password'; placeholder: string }[];
  message: string;
  values: Record<string, string>;
}

export function useMissingCredentialPrompt() {
  const [prompt, setPrompt] = useState<CredentialPromptState | null>(null);

  /**
   * Open from a MissingCredentialError. Works with both predefined and custom integrations.
   * For custom integrations (not in CREDENTIAL_REQUIREMENTS), uses the fields from the error directly.
   */
  const handleError = (error: any) => {
    if (isMissingCredentialError(error)) {
      const req = CREDENTIAL_REQUIREMENTS[error.integration];

      if (req) {
        // Predefined integration — use static field definitions
        const fieldsToShow = req.fields.filter((f) => error.fields.includes(f.key));
        setPrompt({
          isOpen: true,
          integration: error.integration,
          integrationLabel: req.label,
          fields: fieldsToShow.length > 0 ? fieldsToShow : req.fields,
          message: error.message,
          values: {},
        });
      } else {
        // Custom integration — use generic fields from the error
        const genericFields = error.fields.map((key: string) => ({
          key,
          label: key.replace(/[_-]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
          type:
            key.includes('password') ||
            key.includes('secret') ||
            key.includes('token') ||
            key.includes('key')
              ? ('password' as const)
              : ('text' as const),
          placeholder: `Enter ${key.replace(/[_-]/g, ' ')}`,
        }));
        setPrompt({
          isOpen: true,
          integration: error.integration,
          integrationLabel: error.integration
            .replace(/[_-]/g, ' ')
            .replace(/\b\w/g, (c: string) => c.toUpperCase()),
          fields: genericFields,
          message: error.message,
          values: {},
        });
      }
    }
  };

  /**
   * Open with explicit field definitions — used for custom integrations fetched from DB
   * or parsed from [CRED_REQUIRED:{...}] markers.
   */
  const openWithFields = (opts: {
    integration: string;
    integrationLabel: string;
    fields: { key: string; label: string; type: 'text' | 'password'; placeholder: string }[];
    message?: string;
  }) => {
    setPrompt({
      isOpen: true,
      integration: opts.integration,
      integrationLabel: opts.integrationLabel,
      fields: opts.fields,
      message: opts.message || `${opts.integrationLabel} credentials required`,
      values: {},
    });
  };

  const closePrompt = () => {
    setPrompt(null);
  };

  const updateValue = (key: string, value: string) => {
    setPrompt((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        values: { ...prev.values, [key]: value },
      };
    });
  };

  const getPromptState = () => prompt;

  return {
    prompt,
    handleError,
    openWithFields,
    closePrompt,
    updateValue,
    getPromptState,
  };
}
